// components/DemoForm.jsx
import React, { useState, useEffect, useRef } from "react";
import "./DemoForm.css";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import { isSpamDemoRequest } from "../utils/demoFormSpam";

const DemoForm = ({ selectedPlan }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    restaurant: "",
    message: "",
    plan: selectedPlan ? selectedPlan.name : "Professional",
    website: "",
  });

  const [status, setStatus] = useState("");
  const formOpenedAt = useRef(Date.now());

  useEffect(() => {
    if (selectedPlan) {
      setFormData((prev) => ({ ...prev, plan: selectedPlan.name }));
    } else {
      setFormData((prev) => ({ ...prev, plan: "Professional" }));
    }
  }, [selectedPlan]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    const { website, ...payload } = formData;
    if (
      isSpamDemoRequest({
        ...payload,
        website,
        submittedTooFast: Date.now() - formOpenedAt.current < 2500,
      })
    ) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        restaurant: "",
        message: "",
        plan: selectedPlan ? selectedPlan.name : "Professional",
        website: "",
      });
      setStatus("success");
      setTimeout(() => setStatus(""), 5000);
      return;
    }

    try {
      const res = await fetch(
        "https://myfoodtown-api.vellsdrive.com/web/sendEnquirey/mail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          restaurant: "",
          message: "",
          plan: selectedPlan ? selectedPlan.name : "Professional",
          website: "",
        });
        setTimeout(() => setStatus(""), 5000);
      } else {
        setStatus(`Error: ${data.message}`);
      }
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="section contact-section">
      <div className="container">
        <h2 className="section-title">
          Schedule a <span className="gradient-text">Free Demo</span>
        </h2>
        <p className="section-subtitle">
          Get in touch with our team. We'll walk you through every feature and
          help you get started in no time.
        </p>

        <div className="contact__grid">
          {/* Form */}
          <form onSubmit={handleSubmit} className="contact__form glass-card">
            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="contact-name">Your Name</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="contact__field">
                <label htmlFor="contact-email">Email Address</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="contact-phone">Phone Number</label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="contact__field">
                <label htmlFor="contact-restaurant">Restaurant Name</label>
                <input
                  id="contact-restaurant"
                  type="text"
                  name="restaurant"
                  placeholder="My Restaurant"
                  value={formData.restaurant}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="contact-message">Message</label>
              <textarea
                id="contact-message"
                name="message"
                placeholder="Tell us about your business..."
                value={formData.message}
                onChange={handleChange}
                rows="4"
              />
            </div>

            <div className="contact__hp" aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <input type="hidden" name="plan" value={formData.plan} />

            <button
              type="submit"
              className="btn btn-primary contact__submit"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? (
                <>
                  <span className="contact__spinner" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Submit Request
                </>
              )}
            </button>

            {status === "success" && (
              <p className="contact__status contact__status--success">
                ✓ Request sent successfully! Our team will contact you soon.
              </p>
            )}
            {status === "error" && (
              <p className="contact__status contact__status--error">
                Something went wrong. Please try again later.
              </p>
            )}
          </form>

          {/* Contact Info */}
          <div className="contact__info">
            <div className="contact__info-card glass-card">
              <div className="contact__info-icon">
                <Phone size={22} />
              </div>
              <div>
                <h4>Call Us</h4>
                <p>+91 8124888145</p>
              </div>
            </div>

            <div className="contact__info-card glass-card">
              <div className="contact__info-icon">
                <Mail size={22} />
              </div>
              <div>
                <h4>Email Us</h4>
                <p>info@shanthitech.com</p>
              </div>
            </div>

            <div className="contact__info-card glass-card">
              <div className="contact__info-icon">
                <MapPin size={22} />
              </div>
              <div>
                <h4>Visit Us</h4>
                <p>Shanthi Technologies, Tamil Nadu, India</p>
              </div>
            </div>

            <div className="contact__info-note">
              <p>
                💡 <strong>14-day free trial</strong> — No credit card required.
                Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoForm;
